"use client";

import { Calendar, ChevronLeft, Clock, Mail, Share2, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import {
  PostsProvider,
  usePosts,
} from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function formatDate(dateStr?: string) {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function getInitials(name?: string) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
}

function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-8">
        <div className="h-8 w-32 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-12 w-full bg-muted/40 animate-pulse rounded-md" />
        <div className="h-6 w-2/3 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-96 w-full bg-muted/40 animate-pulse rounded-md" />
        <div className="space-y-3">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              data-index={i}
              className="h-4 w-full bg-muted/40 animate-pulse rounded-md"
            />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl text-center py-16">
        <p className="text-muted-foreground">Unable to load the article.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl text-center py-16">
        <p className="text-muted-foreground">Article not found.</p>
      </div>
    );
  }

  const post = posts[0];
  const authorName = post.author?.name || "InfraPM Team";
  const authorAvatar =
    post.author?.avatar?.["96"] || post.author?.avatar?.["48"];

  return (
    <article className="mx-auto max-w-4xl">
      <div className="mb-8">
        <Link
          to="/ResourceCenterPage"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          <span>Back to Resource Center</span>
        </Link>
      </div>

      {post.categories && post.categories.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-2">
          {post.categories.map((cat, i) => (
            <span
              key={cat.id}
              data-index={i}
              className="inline-flex items-center px-3 py-1 text-xs font-medium bg-secondary text-secondary-foreground rounded-md"
            >
              {cat.name}
            </span>
          ))}
        </div>
      )}

      <h1 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight tracking-tight mb-6">
        {post.title}
      </h1>

      {post.excerpt && (
        <div
          className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8"
          dangerouslySetInnerHTML={{ __html: post.excerpt }}
        />
      )}

      <div className="flex flex-wrap items-center gap-6 mb-10 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          <span>{formatDate(post.date)}</span>
        </div>
        <div className="flex items-center gap-2">
          <User className="h-4 w-4" />
          <span>{authorName}</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4" />
          <span>5 min read</span>
        </div>
      </div>

      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-lg shadow-lg">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>
      )}

      <Separator className="mb-10" />

      <div
        className="prose prose-lg max-w-none font-default text-foreground leading-relaxed [&_p]:mb-6 [&_p]:text-base md:[&_p]:text-lg [&_p]:leading-relaxed [&_h2]:font-default [&_h2]:text-3xl [&_h2]:font-bold [&_h2]:mt-12 [&_h2]:mb-4 [&_h2]:text-foreground [&_h3]:font-default [&_h3]:text-2xl [&_h3]:font-semibold [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:text-foreground [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:opacity-80 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-6 [&_ul]:space-y-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-6 [&_ol]:space-y-2 [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:text-muted-foreground [&_blockquote]:my-8 [&_img]:rounded-lg [&_img]:my-8 [&_img]:shadow-md"
        dangerouslySetInnerHTML={{
          __html:
            post.content ||
            "<p>Content is currently unavailable. Please check back for more insights from the InfraPM team.</p>",
        }}
      />

      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-foreground mr-2">
              Tags:
            </span>
            {post.tags.map((tag, i) => (
              <span
                key={tag.id}
                data-index={i}
                className="inline-flex items-center px-3 py-1 text-xs bg-secondary text-secondary-foreground rounded-md hover:bg-accent transition-colors"
              >
                #{tag.name}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 flex items-center justify-between gap-4 py-6 border-y border-border">
        <span className="text-sm font-medium text-foreground">
          Share this insight
        </span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" aria-label="Share">
            <Share2 className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" aria-label="Email">
            <Mail className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Card className="mt-12 bg-secondary/50 border-border">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <Avatar className="h-20 w-20 rounded-lg">
              {authorAvatar && (
                <AvatarImage src={authorAvatar} alt={authorName} />
              )}
              <AvatarFallback className="rounded-lg bg-primary text-primary-foreground text-xl font-semibold">
                {getInitials(authorName)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                Written by
              </p>
              <h3 className="font-default text-xl font-semibold text-foreground mb-2">
                {authorName}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Expert consultant at InfraPM, specializing in infrastructure delivery, grant compliance, and strategic project management for public-sector agencies.
              </p>
              {post.author?.link && (
                <Link
                  to={post.author.link}
                  className="inline-block mt-3 text-sm text-primary underline underline-offset-4 hover:opacity-80"
                >
                  View all insights
                </Link>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            data-index={i}
            className="h-64 bg-muted/40 animate-pulse rounded-lg"
          />
        ))}
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <p className="text-center text-muted-foreground">
        No related insights available at this time.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {posts.slice(0, 3).map((post, i) => (
        <Card
          key={post.id}
          data-index={i}
          className="overflow-hidden border-border hover:shadow-lg transition-shadow group"
        >
          {post.featuredImage && (
            <div className="aspect-[16/10] overflow-hidden bg-muted">
              <img
                src={post.featuredImage}
                alt={post.title}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
          )}
          <CardContent className="pt-2">
            {post.categories && post.categories.length > 0 && (
              <p className="text-xs uppercase tracking-wider text-primary font-semibold mb-2">
                {post.categories[0].name}
              </p>
            )}
            <h3 className="font-default text-lg font-semibold text-foreground mb-2 line-clamp-2 leading-snug">
              {post.title}
            </h3>
            <p className="text-xs text-muted-foreground mb-4">
              {formatDate(post.date)}
            </p>
            <Link
              to={post.link || "#"}
              className="inline-flex items-center gap-1 text-sm font-medium text-foreground hover:text-primary transition-colors"
            >
              <span>Read more</span>
              <ChevronLeft className="h-4 w-4" />
            </Link>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function Article() {
  const currentWPQueryParams = (window as any).wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  const relatedQuery = new WP_Query({
    post_type: "post",
    per_page: 3,
    orderby: "date",
    order: "desc",
  });

  return (
    <section data-section-id="21526"
      id="single-post-template-section"
      className="bg-background text-foreground py-16 md:py-24 px-4 sm:px-6 lg:px-8"
    >
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>

      <div className="mx-auto max-w-6xl mt-20 md:mt-28">
        <div className="mb-10 text-center">
          <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-3">
            Explore Further
          </p>
          <h2 className="font-default text-3xl md:text-4xl font-bold text-foreground">
            Related Insights
          </h2>
          <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
            Discover more expert perspectives and project management strategies from InfraPM.
          </p>
        </div>
        <PostsProvider wp_query={{ post_type: "post", per_page: 3, orderby: "date", order: "desc" }}>
          <RelatedPostsConsumer />
        </PostsProvider>
      </div>
    </section>
  );
}